import type { AlchemyType } from '@/domain/genetics/types';
import type { WorldFacing } from '@/domain/world/actor-presentation';

export type ActorVisualAction =
  | 'idle'
  | 'walk'
  | 'dodge'
  | 'attack'
  | 'skill'
  | 'hurt'
  | 'defeated';

export type ActorActionCommand =
  | { kind: 'attack' | 'dodge' | 'hurt' | 'defeated'; facing: WorldFacing }
  | { kind: 'skill'; facing: WorldFacing; element: AlchemyType };

export interface ActorActionSnapshot {
  kind: ActorVisualAction;
  facing: WorldFacing;
  element?: AlchemyType;
  startedAt: number;
  until: number;
}

const DURATION_MS: Readonly<Record<Exclude<ActorVisualAction, 'idle' | 'walk'>, number>> = {
  dodge: 220,
  attack: 320,
  skill: 520,
  hurt: 260,
  defeated: Number.POSITIVE_INFINITY,
};

const PRIORITY: Readonly<Record<ActorVisualAction, number>> = {
  idle: 0,
  walk: 1,
  dodge: 2,
  attack: 3,
  skill: 4,
  hurt: 5,
  defeated: 6,
};

export class ActorActionStateMachine {
  private current: ActorActionSnapshot;

  constructor(initialFacing: WorldFacing = 'down') {
    this.current = {
      kind: 'idle',
      facing: initialFacing,
      startedAt: 0,
      until: 0,
    };
  }

  play(command: ActorActionCommand, nowMs: number): boolean {
    const now = Math.max(0, nowMs);
    this.expire(now, false);
    if (this.current.until > now && PRIORITY[command.kind] < PRIORITY[this.current.kind]) return false;
    this.current = {
      kind: command.kind,
      facing: command.facing,
      ...('element' in command ? { element: command.element } : {}),
      startedAt: now,
      until: now + DURATION_MS[command.kind],
    };
    return true;
  }

  update(nowMs: number, moving: boolean): ActorActionSnapshot {
    this.expire(Math.max(0, nowMs), moving);
    return { ...this.current };
  }

  private expire(nowMs: number, moving: boolean): void {
    if (this.current.until > nowMs) return;
    this.current = {
      kind: moving ? 'walk' : 'idle',
      facing: this.current.facing,
      startedAt: nowMs,
      until: nowMs,
    };
  }
}
